import React from "react";

import { useTranslation } from "react-i18next";

import { GeoFieldBase } from "@/src/components/form-fields/GeoField";
import {
  RestrictionPeriodFieldBase,
  toTemporalRestrictionFields,
} from "@/src/components/form-fields/RestrictionPeriodField";
import { TextFieldBase } from "@/src/components/form-fields/TextField";

import type { JSX } from "react";

import type { GeoSelection } from "@/src/components/form-fields/GeoField";
import type { RestrictionPeriodChange } from "@/src/components/form-fields/RestrictionPeriodField";
import type {
  RestrictionValue,
  SimpleRestrictionValue,
} from "@/src/utils/mediaRights";

interface RightsRestrictionFieldsProps {
  value: SimpleRestrictionValue &
    Partial<Pick<RestrictionValue, "views_count">>;
  showViewsCount?: boolean;
  disabled?: boolean;
  onChange: (patch: Partial<RestrictionValue>) => void;
}

export default function RightsRestrictionFields({
  value,
  showViewsCount,
  disabled,
  onChange,
}: RightsRestrictionFieldsProps): JSX.Element {
  const { t, i18n } = useTranslation("editor-content");

  const handleSelectionChange = ({
    countries,
    countryGroups,
  }: GeoSelection): void => {
    onChange({
      countries: countries.map((country) => country.uuid),
      country_groups: countryGroups.map((countryGroup) => countryGroup.uuid),
    });
  };

  const handleCommercialChange = ({
    checked,
    beginDate,
    endDate,
  }: RestrictionPeriodChange): void => {
    onChange({
      commercial_restriction: checked,
      commercial_restriction_begin_date: beginDate,
      commercial_restriction_end_date: endDate,
    });
  };

  const handleViewsCountChange = (rawValue: string): void => {
    onChange({ views_count: rawValue === "" ? null : Number(rawValue) });
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="max-w-[500px]">
        <GeoFieldBase
          disabled={disabled}
          geoblockMode={value.geoblock_mode}
          onModeChange={(mode) => onChange({ geoblock_mode: mode })}
          onSelectionChange={handleSelectionChange}
          selectedCountries={value.countries.map((uuid) => ({ uuid }))}
          selectedCountryGroups={value.country_groups.map((uuid) => ({
            uuid,
          }))}
        />
      </div>
      <RestrictionPeriodFieldBase
        beginDate={value.begin_date}
        checked={value.temporal_restriction}
        disabled={disabled}
        endDate={value.end_date}
        label={t("availability")}
        lang={i18n.language}
        onChange={(change) => onChange(toTemporalRestrictionFields(change))}
      />
      <RestrictionPeriodFieldBase
        enablePastStartDate
        beginDate={value.commercial_restriction_begin_date}
        checked={value.commercial_restriction}
        disabled={disabled}
        endDate={value.commercial_restriction_end_date}
        label={t("commercial")}
        lang={i18n.language}
        onChange={handleCommercialChange}
      />
      {showViewsCount ? (
        <div className="max-w-[500px]">
          <TextFieldBase
            disabled={disabled}
            label={t("viewsCount")}
            onChange={handleViewsCountChange}
            placeholder={t("viewsCountPlaceholder")}
            type="number"
            value={String(value.views_count ?? "")}
          />
        </div>
      ) : null}
    </div>
  );
}
